/**
 * Calculates the monthly annuity (interest + principal) based on:
 *  - principal: (amount + startupFee)
 *  - annualNominalRate: e.g. 0.119 for 11.9%
 *  - months: number of periods (e.g. 60)
 */
type CalculateMonthlyAnnuity = {
  principal: number;
  annualNominalRate: number;
  months: number;
  round?: boolean;
};

export function calculateMonthlyAnnuity({
  principal,
  annualNominalRate,
  months,
  round = false,
}: CalculateMonthlyAnnuity) {
  const monthlyRate = annualNominalRate / 100 / 12;

  // Annuity formula (English 'PMT'):
  // pmt = r * PV / [1 - (1+r)^(-n)]
  const result =
    (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));

  if (round) {
    return Math.round(result);
  }
  return result;
}

/**
 * Example of a simple IRR calculation (monthly),
 * based on cash flow in the `flows` array.
 *  flows[0] = + amount at t=0
 *  flows[1..n] = negative payment amounts at t=1..n
 *
 * You can replace this with an IRR library if desired.
 */

export function calculateIRR(flows: number[], guess = 0.1) {
  // Newton-Raphson (for simplicity)
  const maxIterations = 100;
  let rate = guess;

  for (let i = 0; i < maxIterations; i++) {
    let npv = 0;
    let npvDerivative = 0;

    for (let t = 0; t < flows.length; t++) {
      const cashflow = flows[t];
      const discount = Math.pow(1 + rate, t);
      npv += cashflow / discount;

      // derived
      if (discount !== 0) {
        npvDerivative -= (t * cashflow) / (discount * (1 + rate));
      }
    }

    const newRate = rate - npv / npvDerivative;

    if (Math.abs(newRate - rate) < 1e-7) {
      rate = newRate;
      break;
    }

    rate = newRate;
  }

  return rate;
}
